import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from './root-store';
import { PersonalAssetWithValueMobxDto } from '../mobx/dtos/personal-assets/personal-asset-with-value-mobx-dto';
import { PersonalAssetSpecialTypeEnum } from '../../defs/api';
import i18n from '../../translation/i18n';

/**
 * InvestownAssetStore – dočasně napojen na stejná data jako PersonalAssetStore.
 */

export class InvestownAssetStore {
    rootStore: RootStore;

    investownAssets: PersonalAssetWithValueMobxDto[] = [];

    loading = false;

    limit = Number(process.env.NEXT_PUBLIC_PAGE_LIMIT);

    totalCount = 0;

    page = 1;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    async fetchInvestownAssetList(portfolioId: string): Promise<void> {
        // Dočasně používá stejný endpoint jako PersonalAssetStore
        this.startLoading();
        const fetchedPersonalAssetList =
            await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerGetList({
                portfolioId,
                sourceType: PersonalAssetSpecialTypeEnum.Investown,
                limit: this.limit,
                offset: (this.page - 1) * this.limit,
            });
        runInAction(() => {
            this.investownAssets = PersonalAssetWithValueMobxDto.createFromArray(fetchedPersonalAssetList.items);
            this.limit = fetchedPersonalAssetList.limit;
            this.totalCount = fetchedPersonalAssetList.total;
            this.stopLoading();
        });
    }

    async setPage(page: number, portfolioId: string): Promise<void> {
        this.page = page;
        await this.fetchInvestownAssetList(portfolioId);
    }

    setPageLimit(limit: number | string | null, portfolioId: string): void {
        if (limit && !Number.isNaN(limit)) {
            this.limit = +limit;
            this.setPage(1, portfolioId);
        }
    }

    get pagesCount(): number {
        return Math.ceil(this.totalCount / this.limit) || 0;
    }

    async deleteByPortfolioId(portfolioId: string, sourceType: PersonalAssetSpecialTypeEnum): Promise<void> {
        this.startLoading();
        try {
            await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerDeleteByPortfolioId({
                portfolioId,
                sourceType,
            });
            await this.fetchInvestownAssetList(portfolioId);
            await this.rootStore.portfolioStore.fetchPortfolioList();

            runInAction(() => {
                this.rootStore.personalAssetStore.personalAssetImportDialogStore.setImportResultMessage(
                    i18n.t(`blocks.personalFinances.deleted.successfully`),
                );
                this.stopLoading();
            });
        } catch (e) {
            runInAction(() => {
                this.rootStore.personalAssetStore.personalAssetImportDialogStore.setImportResultMessage(
                    i18n.t(`blocks.personalFinances.deleted.failed`),
                );
                this.stopLoading();
            });
        }
    }
}
